import { CreditRuleCoin } from '@aibees/elephant/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useHandleUnlockEmails } from '../../hooks/use-handle-unlock-emails';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockEmailsProps as Props } from './types';

export const UnlockEmails = memo(
  ({ className, compact, item, setLoading }: Props) => {
    const hover = useBoolean(false);

    const {
      handleReverify,
      handleUpdate,
      hasUpdate,
      needToCheckForUpdate,
      showReverify,
      unlocked,
      loading,
      handleUnlock,
      handleCheckForUpdate,
      creditType,
    } = useHandleUnlockEmails(item);

    useEffect(() => {
      setLoading?.(loading);
    }, [loading]);

    const action = useMemo(() => {
      if (!unlocked) return { label: 'Unlock', handler: handleUnlock };
      if (hasUpdate) return { label: 'Update', handler: handleUpdate };
      if (needToCheckForUpdate)
        return { label: 'Check for Update', handler: handleCheckForUpdate };
      if (showReverify) return { label: 'Re-Verify', handler: handleReverify };
      return null;
    }, [
      unlocked,
      hasUpdate,
      needToCheckForUpdate,
      showReverify,
      handleUnlock,
      handleUpdate,
      handleCheckForUpdate,
      handleReverify,
    ]);

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      action?.handler(e);
      hover.onFalse();
    };

    // Preserve last action's label during loading so the button keeps its
    // natural width (MUI hides the text via `color: transparent` but the width
    // is still occupied) and we avoid a layout jump on the way out.
    const lastActionRef = useRef(action);
    if (action) lastActionRef.current = action;
    const displayLabel = action?.label ?? lastActionRef.current?.label;

    return (
      <Styled.Wrapper
        className={classnames('UnlockEmails', className)}
        sx={SX.root(!!compact)}
        direction="row"
        justifyContent={compact ? 'unset' : 'space-between'}
        alignItems="center"
        gap={0.5}
      >
        {hover.value && !loading && (
          <CreditRuleCoin type={creditType} sx={SX.creditTag} />
        )}

        {(action || loading) && (
          <LoadingButton
            variant="contained"
            color="primary"
            size="xsmall"
            loading={loading}
            onClick={handleClick}
            onMouseEnter={hover.onTrue}
            onMouseLeave={hover.onFalse}
          >
            {displayLabel}
          </LoadingButton>
        )}
      </Styled.Wrapper>
    );
  }
);

UnlockEmails.displayName = 'UnlockEmails';
